import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/App";
import { CheckCircle, XCircle, Loader2, ArrowLeft, CreditCard, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentStatus = () => {
  const { language } = useLanguage();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const paymentResult = params.get("payment");

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sid}`);
      if (!response.ok) throw new Error("Failed to check status");
      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      setStatus("processing");
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch (error) {
      console.error("Status check error:", error);
      if (attempt < 4) {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    if (paymentResult === "cancelled") {
      setStatus("cancelled");
      return;
    }

    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus("none");
    }
  }, [sessionId, paymentResult, pollStatus]);

  if (status === "none") return null;

  const texts = {
    success: {
      title_en: "Payment Successful!",
      title_nl: "Betaling Geslaagd!",
      subtitle_en: "Thank you for your purchase. We'll be in touch within 24 hours to get started on your project.",
      subtitle_nl: "Bedankt voor je aankoop. We nemen binnen 24 uur contact op om je project te starten."
    },
    cancelled: {
      title_en: "Payment Cancelled",
      title_nl: "Betaling Geannuleerd",
      subtitle_en: "No worries! You can try again whenever you're ready.",
      subtitle_nl: "Geen zorgen! Je kunt het opnieuw proberen wanneer je wilt."
    },
    processing: {
      title_en: "Processing Payment...",
      title_nl: "Betaling Verwerken...",
      subtitle_en: "Please wait while we confirm your payment.",
      subtitle_nl: "Even geduld terwijl we je betaling bevestigen."
    },
    loading: {
      title_en: "Checking Payment...",
      title_nl: "Betaling Controleren...",
      subtitle_en: "Please wait...",
      subtitle_nl: "Even geduld..."
    },
    error: {
      title_en: "Something Went Wrong",
      title_nl: "Er Ging Iets Mis",
      subtitle_en: "Please contact us via WhatsApp or our contact form if your payment was charged.",
      subtitle_nl: "Neem contact op via WhatsApp of ons contactformulier als je betaling is afgeschreven."
    },
    expired: {
      title_en: "Session Expired",
      title_nl: "Sessie Verlopen",
      subtitle_en: "Your payment session has expired. Please try again.",
      subtitle_nl: "Je betalingssessie is verlopen. Probeer het opnieuw."
    },
    timeout: {
      title_en: "Status Check Timed Out",
      title_nl: "Statuscontrole Verlopen",
      subtitle_en: "Your payment may still be processing. Check your email for confirmation.",
      subtitle_nl: "Je betaling wordt mogelijk nog verwerkt. Check je e-mail voor bevestiging."
    }
  };

  const t = texts[status] || texts.error;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="payment-status-banner"
      className={`fixed top-20 left-0 right-0 z-40 px-6 ${status === "none" ? "hidden" : ""}`}
    >
      <div className={`max-w-2xl mx-auto p-6 border backdrop-blur-xl ${
        status === "success"
          ? "bg-green-950/90 border-green-500/30"
          : status === "cancelled" || status === "expired"
          ? "bg-zinc-900/90 border-zinc-700"
          : status === "error" || status === "timeout"
          ? "bg-red-950/90 border-red-500/30"
          : "bg-[#0A0A0A]/95 border-[#FF6B00]/30"
      }`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            {status === "success" && <CheckCircle size={24} className="text-green-400" />}
            {(status === "cancelled" || status === "expired") && <XCircle size={24} className="text-zinc-400" />}
            {(status === "error" || status === "timeout") && <XCircle size={24} className="text-red-400" />}
            {(status === "processing" || status === "loading") && <Loader2 size={24} className="text-[#FF6B00] animate-spin" />}
          </div>
          <div className="flex-grow">
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {language === "nl" ? t.title_nl : t.title_en}
            </h3>
            <p className="text-zinc-400 text-sm mt-1">
              {language === "nl" ? t.subtitle_nl : t.subtitle_en}
            </p>
            {paymentData && status === "success" && (
              <p className="text-green-400 text-sm mt-2 font-medium">
                {paymentData.metadata?.package_name} - €{(paymentData.amount_total / 100).toFixed(2)}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              window.history.replaceState({}, document.title, window.location.pathname);
              window.location.reload();
            }}
            className="text-zinc-400 hover:text-white transition-colors text-sm font-medium flex-shrink-0"
          >
            {language === "nl" ? "Sluiten" : "Close"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PaymentStatus;
